'use client';

import { keyCodes } from '@/constants/keyCode';
import { Dispatch, PropsWithChildren, SetStateAction, createContext, useEffect, useMemo, useState } from 'react';

type InputType = 'MOUSE' | 'KEYBOARD' | 'TOUCH';

export const InputTypeContext = createContext<{
  inputType: InputType;
  setInputType: Dispatch<SetStateAction<InputType>>;
} | null>(null);

export type InputTypeContextProviderProps = PropsWithChildren;

export const InputTypeContextProvider = ({ children }: InputTypeContextProviderProps) => {
  const [inputType, setInputType] = useState<InputType>('KEYBOARD');

  const contextValue = useMemo(() => ({ inputType, setInputType }), [inputType, setInputType]);

  const className = useMemo(() => {
    return inputType === 'MOUSE' ? 'mouse-controller' : 'keyboard-controller';
  }, [inputType]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.keyCode) {
        case keyCodes.DOWN:
        case keyCodes.UP:
        case keyCodes.LEFT:
        case keyCodes.RIGHT:
        case 37:
        case 38:
        case 39:
        case 40: {
          setInputType('KEYBOARD');
          break;
        }
      }
    };

    const handleMouseMove = () => {
      setInputType((prev) => {
        if (prev !== 'TOUCH') {
          return 'MOUSE';
        } else {
          return prev;
        }
      });
    };

    const handleTouch = (event: TouchEvent) => {
      event.stopPropagation();
      setInputType('TOUCH');
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchstart', handleTouch);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchstart', handleTouch);
    };
  }, []);

  return (
    <InputTypeContext.Provider value={contextValue}>
      <div className={className}>{children}</div>
    </InputTypeContext.Provider>
  );
};
